import { call, put, takeLatest, select } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import {
  ICreateSubordinate,
  IEditSubordinatePermissions,
  IGetSubordinateDetailsPayload,
  IGetSubordinatesParams,
  IGetSubordinatesResponse,
  IValidateSubordinate,
  subordinateActions,
} from './subordinateSlice';
import {
  createSubordinate,
  updateSubordinatePermissions,
  deleteSubordinate,
  getSubordinateDetails,
  getSubordinates,
  validateOrganisation,
} from '../../api/subordinateApi';
import { RootState } from '../store';

import { toastStyles } from '../../utils/styles';
import { userActions } from '../user/userSlice';

function* watchValidateSubordinateOrganization({ payload }: { payload: IValidateSubordinate }) {
  try {
    yield put(subordinateActions.setComponentStatus({ validateSubordinateOrganizationLoading: true }));
    const res: AxiosResponse = yield call(validateOrganisation, payload);
    yield put(subordinateActions.setSubordinate(res.data));
    yield put(subordinateActions.setComponentStatus({ activeStep: '1' }));
    yield put(subordinateActions.setComponentStatus({ validateSubordinateOrganizationLoading: false }));
  } catch (error: any) {
    yield put(
      subordinateActions.setComponentStatus({
        validationError: 'Company name not recognized. Please try again or contact your Administrator.',
        validateSubordinateOrganizationLoading: false,
        validateSubordinateOrganizationSuccess: false,
      }),
    );
  }
}

function* watchCreateSubordinate({ payload }: { payload: ICreateSubordinate }) {
  try {
    yield put(subordinateActions.setComponentStatus({ createSubordinateLoading: true }));
    const res: AxiosResponse = yield call(createSubordinate, payload);
    yield put(subordinateActions.setSubordinate(res.data));
    yield put(subordinateActions.setComponentStatus({ activeStep: '2' }));
    yield put(subordinateActions.setComponentStatus({ createSubordinateLoading: false }));
  } catch (error: any) {
    yield put(
      subordinateActions.setComponentStatus({
        creationError: 'Failed to Create Account. Please Try Again.',
        createSubordinateLoading: false,
        createSubordinateSuccess: false,
      }),
    );
  }
}

const getFilters = (state: RootState) => state.subordinate.filters;

function* watchFetchSubordinates() {
  try {
    const filters: IGetSubordinatesParams = yield select(getFilters);
    const res: AxiosResponse<IGetSubordinatesResponse> = yield call(getSubordinates, filters);
    yield put(subordinateActions.fetchSubordinatesSuccess(res.data));
  } catch (error: any) {
    yield put(subordinateActions.fetchSubordinatesFailure(error.message));
  }
}

function* watchGetSubordinateDetails({ payload }: { payload: IGetSubordinateDetailsPayload }) {
  try {
    yield put(subordinateActions.setComponentStatus({ getSubordinateDetailsLoading: true }));
    const res: AxiosResponse = yield call(getSubordinateDetails, payload);
    yield put(subordinateActions.setSubordinate(res.data));
    yield put(subordinateActions.setComponentStatus({ getSubordinateDetailsLoading: false }));
  } catch (error) {
    yield put(subordinateActions.setComponentStatus({ getSubordinateDetailsLoading: false }));
    toast.error('Error Retrieving Details.Please Try Again', toastStyles.error);
  }
}

function* watchEditSubordinatePermissions({ payload }: { payload: IEditSubordinatePermissions }) {
  try {
    yield put(subordinateActions.setComponentStatus({ getSubordinateDetailsLoading: true }));
    const res: AxiosResponse = yield call(updateSubordinatePermissions, payload);
    yield put(subordinateActions.setSubordinate(res.data));
    yield put(subordinateActions.setComponentStatus({ getSubordinateDetailsLoading: false }));
    toast.success('Permissions Updated Successfully', toastStyles.success);
  } catch (error) {
    yield put(subordinateActions.setComponentStatus({ getSubordinateDetailsLoading: false }));
    toast.error('Error Updating Permissions.Please Try Again', toastStyles.error);
  }
}
function* watchDeleteSubordinate({ payload }: { payload: { userId: string } }) {
  const { userId } = payload;
  try {
    yield put(subordinateActions.setComponentStatus({ deleteSubordinateLoading: true }));
    yield call(deleteSubordinate, userId);

    yield put(
      subordinateActions.setComponentStatus({
        deleteSubordinateLoading: false,
        isDeleteSubordinateModalOpen: false,
      }),
    );
    toast.success('Subordinate Deleted!', toastStyles.success);
    yield put(subordinateActions.getSubordinatesDetails({ userId }));
    const filters: IGetSubordinatesParams = yield select(getFilters);
    yield put(subordinateActions.fetchSubordinatesRequest(filters));
    yield put(userActions.getUserAsync());
  } catch (error: any) {
    yield put(
      subordinateActions.setComponentStatus({
        error: error.response?.data?.message || 'Failed to delete subordinate',
        deleteSubordinateLoading: false,
      }),
    );
    toast.error(error.response?.data?.message || 'Failed to delete subordinate', toastStyles.error);
  }
}

export function* watchSubordinateSagas() {
  yield takeLatest(subordinateActions.validateSubordinateOrganizationAsync, watchValidateSubordinateOrganization);
  yield takeLatest(subordinateActions.createSubordinateAsync, watchCreateSubordinate);
  yield takeLatest(subordinateActions.fetchSubordinatesRequest, watchFetchSubordinates);
  yield takeLatest(subordinateActions.getSubordinatesDetails, watchGetSubordinateDetails);
  yield takeLatest(subordinateActions.EditSubordinatePermissionsAsync, watchEditSubordinatePermissions);
  yield takeLatest(subordinateActions.deleteSubordinateAsync, watchDeleteSubordinate);
}
const subordinateSagas = watchSubordinateSagas;
export default subordinateSagas;
